import React, { useEffect, useState } from "react";
import {
  fetchSchedulableAssets,
  attachToSchedule,
} from "./contentStudioApi";
import "./ContentStudio.css";

/**
 * Permission-aware Media Asset picker for Timeline Studio.
 * Source-agnostic: creator / organization / platform / shared.
 */
function MediaAssetPicker({ open, onClose, onSelect, actor = {} }) {
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchSchedulableAssets(
          Object.assign({}, actor, {
            q: q || undefined,
            origin: origin || undefined,
            for_schedule: true,
            limit: 50,
          })
        );
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Unable to load assets");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, q, origin, actor.organization_id, actor.creator_id]);

  if (!open) return null;

  const choose = async (item) => {
    const attached = await attachToSchedule(
      Object.assign({}, actor, {
        admin_video_id: item.admin_video_id,
        media_asset_key: item.media_asset_key || item.id,
      })
    );
    if (attached.success && attached.data) {
      onSelect(attached.data);
      onClose();
    } else {
      onSelect({
        admin_video_id: item.admin_video_id,
        media_asset_key: item.media_asset_key || item.id,
        media_asset: item,
        title: item.title,
      });
      onClose();
    }
  };

  return (
    <div className="cstudio-modal" role="dialog" aria-label="Schedule Media Asset">
      <div className="cstudio-modal__panel">
        <header className="cstudio-modal__header">
          <div>
            <p className="cstudio-eyebrow">Permission-aware library</p>
            <h2>Schedule Media Asset</h2>
          </div>
          <button type="button" className="cstudio-btn cstudio-btn--ghost" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="cstudio-modal__filters">
          <input
            type="search"
            placeholder="Search published assets…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={origin} onChange={(e) => setOrigin(e.target.value)}>
            <option value="">All (videos + adapters)</option>
            <option value="creator">Creator videos</option>
            <option value="organization">Organization videos</option>
            <option value="platform">Platform videos</option>
            <option value="series">Series</option>
            <option value="season">Seasons</option>
            <option value="playlist">Playlists / blocks</option>
            <option value="collection">Collections</option>
          </select>
        </div>
        {loading && <p className="cstudio-muted">Loading…</p>}
        {error && <p className="cstudio-error">{error}</p>}
        <ul className="cstudio-picker-list">
          {items.map((item) => (
            <li key={item.media_asset_key || item.id}>
              <button type="button" onClick={() => choose(item)}>
                <strong>{item.title || "Untitled"}</strong>
                <span>
                  {(item.metadata && item.metadata.adapter_kind) ||
                    (item.ownership && item.ownership.origin) ||
                    "—"}{" "}
                  · {(item.workflow && item.workflow.status) || "published"} ·{" "}
                  {item.presentationKind}
                  {item.collection && item.collection.item_count != null
                    ? ` · ${item.collection.item_count} items`
                    : ""}
                </span>
              </button>
            </li>
          ))}
          {!loading && !items.length && (
            <li className="cstudio-muted">No schedulable Media Assets in your permission scope.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default MediaAssetPicker;
